import { useAuth0 } from '@auth0/auth0-react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  AppBar,
  Avatar,
  Box,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material'
import Cookies from 'js-cookie'
import upperCase from 'lodash/upperCase'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import navbar from '@/assets/data/navbar'
import logo from '@/assets/images/Matisse_logo.png'
import Button from '@/components/Button'
import NavMenu from '@/components/NavMenu'
import { useBreakpoints } from '@/hooks/useBreakpoints'
import { persistor } from '@/store'
import { clearAllInOneState } from '@/store/allInOne'
import {
  apiService,
  useLazyClearSessionQuery,
  useLazyGetStripePlansQuery,
} from '@/store/apiService'
import { clearAppState, setIsLoggingOut } from '@/store/appSlice'
import { resetProjectState } from '@/store/project'
import { stringToColor } from '@/utils'
import { getProfileApi } from '@/utils/appApi'
import { NAVBAR_HEIGHT } from '@/utils/constants'
import { resetHeaders } from '@/utils/httpService'

import LanguageDialog from './LanguageDialog'

interface ProfileMenuItem {
  id: number
  label: string | React.ReactNode
  route?: string
  onClick?: () => void
}

const NavigationBar = () => {
  const { user, isAuthenticated, logout } = useAuth0()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [clearSession] = useLazyClearSessionQuery()
  const [stripePlan] = useLazyGetStripePlansQuery()
  const { i18n } = useTranslation('translation')

  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false)

  const { t } = useTranslation('translation', {
    keyPrefix: 'Navbar',
  })

  const [profileResponse, setProfileResponse] = useState<any>(null)

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await getProfileApi()
        setProfileResponse(data)
      } catch (error) {
        console.error('Failed to fetch profile:', error)
      }
    }

    fetchProfile()

    const handleProfileUpdate = () => {
      fetchProfile()
    }

    window.addEventListener('profileUpdated', handleProfileUpdate)

    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate)
    }
  }, [])

  const remainingTrialDays =
    profileResponse?.subscriptions?.[0]?.remaining_trial_days

  const theme = useTheme()
  const handleLogout = async () => {
    try {
      // Clear API session
      dispatch(setIsLoggingOut(true))
      await clearSession('logout')

      // Clear RTK Query cache
      dispatch(apiService.util.resetApiState())

      // Clear all Redux states
      dispatch(clearAppState())
      dispatch(clearAllInOneState())
      dispatch(resetProjectState())

      // Clear Redux persist storage
      await persistor.purge()

      // Clear all cookies
      const cookies = Cookies.get()
      Object.keys(cookies).forEach((cookieName) => {
        Cookies.remove(cookieName)
      })
      Cookies.remove('session_id')
      // Clear localStorage and sessionStorage
      window.localStorage.clear()
      window.sessionStorage.clear()

      // Reset HTTP headers
      resetHeaders()

      // Logout from Auth0
      logout({
        logoutParams: { returnTo: window.location.origin },
      })
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  const getStripePlans = async () => {
    try {
      const response = await stripePlan(undefined)
      if (response.data && (response.data as any).url) {
        window.open((response.data as any).url, '_blank')
      }
    } catch (error) {
      console.error('Error fetching Stripe plans:', error)
    }
  }

  const profileMenuItems: ProfileMenuItem[] = [
    {
      id: 0,
      label: (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            {`Hello, ${profileResponse?.name}`}
          </Typography>
          <Typography variant="body2" color="#D9D9D9">
            {user?.email}
          </Typography>
        </Box>
      ) as React.ReactNode,
      route: '/profile',
    },
    { id: 1, label: t('dropdownMenu.plans'), route: '/plans' },
    {
      id: 2,
      label: t('dropdownMenu.subscription'),
      onClick: () => {
        void getStripePlans()
      },
    },
    {
      id: 3,
      label: t('dropdownMenu.webshop'),
      onClick: () => window.open('https://www.matisse.ai/webshop/', '_blank'),
    },
    {
      id: 6,
      label: 'Language',
      onClick: () => setIsLanguageDialogOpen(true),
    },
    {
      id: 4,
      label: 'divider',
    },
    {
      id: 5,
      label: t('dropdownMenu.logout'),
      onClick: () => {
        void handleLogout()
      },
    },
  ]

  const handleNavigation = (route: string) => {
    navigate({
      pathname: route,
      search: window.location.search,
    })
  }
  const { isDisplay1280 } = useBreakpoints()
  const padding = isDisplay1280 ? '0px 16px' : '0px 8px'

  return (
    <AppBar
      position="static"
      sx={{
        background: theme.palette.background.default,
        height: `${NAVBAR_HEIGHT}px`,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          display: 'flex',
          justifyContent: 'space-evenly',
          color: theme.palette.text.primary,
        }}
      >
        <Typography
          noWrap
          component="a"
          href="/"
          sx={{ textDecoration: 'none' }}
        >
          <img src={logo} alt="matisse" style={{ height: 35 }} />
        </Typography>

        <Button
          label={t('navlink.myProjects')}
          onClick={() => handleNavigation('/projects')}
          variant="text"
          color="default"
          size="large"
          sx={{ padding: padding }}
        />

        <NavMenu
          menuOptions={navbar.getRecipe}
          label={t('navlink.getRecipe')}
          buttonSx={{ padding: padding }}
        />

        <NavMenu
          menuOptions={navbar.wizards}
          label={t('navlink.wizards')}
          buttonSx={{ padding: padding }}
        />

        <NavMenu
          menuOptions={navbar.stainingStudioRecipe}
          label={t('navlink.stainingStudio')}
          buttonSx={{ padding: padding }}
        />

        <NavMenu
          menuOptions={navbar.digitalData}
          label={t('navlink.digitalTryIn')}
          buttonSx={{ padding: padding }}
          renderDiamondIcon={false}
        />

        {isAuthenticated && (
          <Box
            sx={{
              display: 'flex',
              gap: isDisplay1280 ? 2 : 0,
              alignItems: 'center',
            }}
          >
            <NavMenu
              menuOptions={profileMenuItems}
              label={
                remainingTrialDays ? (
                  <span className="gradient-label">
                    {`${remainingTrialDays} days remaining`}
                  </span>
                ) : (
                  ''
                )
              }
              buttonSx={{
                padding: padding,
                ...(!isDisplay1280
                  ? {
                      maxWidth: '200px',
                      wordBreak: 'break-word',
                    }
                  : { maxWidth: 'auto' }),
                '& .gradient-label': {
                  background:
                    'linear-gradient(90deg, #CE81FF 0%, #3FD8FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block',
                },
              }}
              endIcon={
                <Box display="flex" alignItems="center">
                  <Avatar
                    src={user?.picture}
                    alt={upperCase(user?.name)}
                    sx={{
                      width: 30,
                      height: 30,
                      fontSize: '1rem',
                      background: stringToColor(user?.name || ''),
                    }}
                  />
                  <ExpandMoreIcon />
                </Box>
              }
              renderDiamondIcon={false}
              disableTranslation
            />
          </Box>
        )}
      </Toolbar>
      <LanguageDialog
        isOpen={isLanguageDialogOpen}
        onClose={() => setIsLanguageDialogOpen(false)}
        profileResponse={profileResponse}
        currentLanguage={i18n.language}
      />
    </AppBar>
  )
}

export default NavigationBar
